import { Box, Tab, Tabs } from "@mui/material";
import { ColetaPrecoFornecedor } from "../../../../domain/models/coleta_preco_fornecedor";
import { useCallback, useMemo } from "react";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { GridColDef } from "@mui/x-data-grid";
import { ColetaProdutoServicoLista } from "../../../../domain/models/coleta_produto_servico_lista";
import { ColetaPrecoFornecedorEditForm } from "../../../schemas/ColetaPrecos_cadastro/coleta_preco_fornecedor_edit";
import { NumericMaskedInput } from "../../../../../../common/components/masked_inputs/numeric_masked_input";
import { DefaultDatePicker } from "../../../../../../common/components/masked_inputs/default_date_picker_input";

import DefaultInput from "../../../../../../common/components/default_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import dayjs from "dayjs";

/**
 * ColetaPrecoFornecedorViewer
 */

type Props = {
  loading: boolean;
  saving: boolean;
  status: number | null;
  colunmnDefinitions: GridColDef[];
  coletaPrecoFornecedores: ColetaPrecoFornecedor[];
  coletaProdutosServicos: ColetaProdutoServicoLista[];
  coletaPrecoFornecedor: ColetaPrecoFornecedorEditForm | null;
  subTotal: number | null;
  total: number | null;
  selectedColetaPrecoFornecedorIdx: number;
  onColetaPrecoFornecedorChange: (tab: number, idColetaPrecoFornecedor: number) => void;
  onImpostoChange: (value: number | null) => void;
  onFreteChange: (value: number | null) => void;
  onDescontoChange: (value: number | null) => void;
  onAcrescimoChange: (value: number | null) => void;
  onDataEntregaChange: (value: Date | null) => void;
  onPrazoEntregaChange: (value: string | null) => void;
  onCondicoesPagamentoChange: (value: string | null) => void;
  onObservacoesChange: (value: string | null) => void;
  onCopyColetaPrecoFornecedor: (openState: boolean) => void;
  onDeleteColetaPrecoFornecedor: (openState: boolean) => void;
  onCancelarAprovacaoColetaPrecoFornecedorSubmit: () => void;
  onConfigurarPagamentoSubmit: () => void;
};

export const ColetaPrecoFornecedorViewer = ({
  loading,
  saving,
  status,
  colunmnDefinitions,
  coletaPrecoFornecedores,
  coletaProdutosServicos,
  coletaPrecoFornecedor,
  subTotal,
  total,
  selectedColetaPrecoFornecedorIdx,
  onColetaPrecoFornecedorChange,
  onImpostoChange,
  onFreteChange,
  onDescontoChange,
  onAcrescimoChange,
  onDataEntregaChange,
  onPrazoEntregaChange,
  onCondicoesPagamentoChange,
  onObservacoesChange,
  onCopyColetaPrecoFornecedor,
  onDeleteColetaPrecoFornecedor,
  onCancelarAprovacaoColetaPrecoFornecedorSubmit,
  onConfigurarPagamentoSubmit,
}: Props) => {
  const selectedColetaPrecoFornecedor = useMemo(() => {
    if (selectedColetaPrecoFornecedorIdx < 1) return null;
    if (selectedColetaPrecoFornecedorIdx > coletaPrecoFornecedores.length) return null;
    return coletaPrecoFornecedores[selectedColetaPrecoFornecedorIdx - 1];
  }, [
    selectedColetaPrecoFornecedorIdx,
    coletaPrecoFornecedores,
  ]);

  const dataGridComponent = useMemo(() => {
    return (
      <StyledDataGrid
        tableName="coleta_precos_cadastro_coleta_preco_fornecedor"
        disableRowSelectionOnClick={true}
        getRowId={(row: ColetaProdutoServicoLista) => row.idColetaProdutoServico}
        columns={colunmnDefinitions}
        loading={loading}
        rows={coletaProdutosServicos}
        autoHeight={true}
      />
    );
  }, [
    loading,
    colunmnDefinitions,
    coletaProdutosServicos,
  ]);

  const handleTabChange = useCallback((tab: number) => {
    if (tab > 0 && tab <= coletaPrecoFornecedores.length) {
      const coletaPrecoFornecedor = coletaPrecoFornecedores[tab - 1]
      onColetaPrecoFornecedorChange(tab, coletaPrecoFornecedor.idColetaPrecoFornecedor);
    }
  }, [
    coletaPrecoFornecedores,
    onColetaPrecoFornecedorChange,
  ]);

  const handleCopy = useCallback(() => {
    if (selectedColetaPrecoFornecedor && !loading && !saving) {
      onCopyColetaPrecoFornecedor(true);
    }
  }, [
    loading,
    saving,
    selectedColetaPrecoFornecedor,
    onCopyColetaPrecoFornecedor,
  ]);

  const handleDelete = useCallback(() => {
    if (selectedColetaPrecoFornecedor && !loading && !saving) {
      onDeleteColetaPrecoFornecedor(true);
    }
  }, [
    loading,
    saving,
    selectedColetaPrecoFornecedor,
    onDeleteColetaPrecoFornecedor,
  ]);

  return (
    <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 rounded border border-border-light-mode dark:border-border-dark-mode text-black dark:text-white">
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          variant="scrollable"
          scrollButtons="auto"
          slotProps={{
            startScrollButtonIcon: {
              className: "text-black dark:text-white",
            },
            endScrollButtonIcon: {
              className: "text-black dark:text-white",
            },
            indicator: {
              style: {
                background: '#009C4E',
              }
            }
          }}
          value={selectedColetaPrecoFornecedorIdx}
          onChange={(_, tab) => handleTabChange(tab)}>
          {
            coletaPrecoFornecedores.map((x, idx) => {
              return (
                <Tab
                  key={x.idColetaPrecoFornecedor}
                  sx={{
                    textTransform: "none",
                    '&.Mui-selected': {
                      color: '#009C4E',
                    },
                  }}
                  label={x.nomeFantasia}
                  value={idx + 1}
                />
              );
            })
          }
        </Tabs>
        <Box sx={{ padding: 4 }}>
          {
            selectedColetaPrecoFornecedor !== null &&
            <div className="w-full flex flex-col gap-4">
              <div className="w-full min-h-[2.5rem] flex flex-row justify-end gap-2">
                {
                  status === 0 &&
                  <>
                    <div className="w-[7.5rem]">
                      <PrimaryButton
                        buttonColor="secondary"
                        onMouseDown={e => {
                          handleCopy();
                          e.preventDefault();
                        }}>
                        Copiar Coleta
                      </PrimaryButton>
                    </div>

                    <div className="w-[7.5rem]">
                      <PrimaryButton
                        buttonColor="red"
                        onMouseDown={e => {
                          handleDelete();
                          e.preventDefault();
                        }}>
                        Excluir Coleta
                      </PrimaryButton>
                    </div>
                  </>
                }
                <>
                  {
                    (status === 3 && selectedColetaPrecoFornecedor.aprovado === true) &&
                    <div className="w-[12.5rem]">
                      <PrimaryButton
                        buttonColor="red"
                        startIcon={<DynamicIcon nameIcon="ThumbDown" size={25} className="text-white" />}
                        onMouseDown={e => {
                          onCancelarAprovacaoColetaPrecoFornecedorSubmit();
                          e.preventDefault();
                        }}>
                        Cancelar Aprovação
                      </PrimaryButton>
                    </div>

                  }
                </>
              </div>

              <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-2 gap-y-2">
                <DefaultInput
                  name='fornNomeFantasia'
                  label='Fornecedor'
                  readOnly={true}
                  value={selectedColetaPrecoFornecedor.nomeFantasia}
                />

                <DefaultInput
                  name='fornTelefone'
                  label='Telefone'
                  readOnly={true}
                  value={selectedColetaPrecoFornecedor.telefoneFixo}
                />

                <DefaultInput
                  name='fornContato'
                  label='Contato'
                  readOnly={true}
                  value={selectedColetaPrecoFornecedor.contato}
                />

                <DefaultInput
                  name='fornDataUltimaCompra'
                  label='Data última compra'
                  readOnly={true}
                  value={selectedColetaPrecoFornecedor.dataUltimaCompra}
                />
              </div>

              {dataGridComponent}

              <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-2">
                <NumericMaskedInput
                  name='fornImporto'
                  label='IMPOSTO'
                  readOnly={status !== 0}
                  value={coletaPrecoFornecedor !== null ? coletaPrecoFornecedor.imposto : null}
                  onChangeValue={e => onImpostoChange(e)}
                />

                <NumericMaskedInput
                  name='fornSubTotal'
                  label='SUB-TOTAL'
                  value={subTotal}
                  readOnly={true}
                  onChangeValue={() => { }}
                />
              </div>

              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <NumericMaskedInput
                  name='fornFrete'
                  label='FRETE'
                  readOnly={status !== 0}
                  value={coletaPrecoFornecedor !== null ? coletaPrecoFornecedor.frete : null}
                  onChangeValue={e => onFreteChange(e)}
                />

                <NumericMaskedInput
                  name='fornDesconto'
                  label='DESCONTO'
                  readOnly={status !== 0}
                  value={coletaPrecoFornecedor !== null ? coletaPrecoFornecedor.desconto : null}
                  onChangeValue={e => onDescontoChange(e)}
                />

                <NumericMaskedInput
                  name='fornAcrescimo'
                  label='ACRÉSCIMO'
                  readOnly={status !== 0}
                  value={coletaPrecoFornecedor !== null ? coletaPrecoFornecedor.acrescimo : null}
                  onChangeValue={e => onAcrescimoChange(e)}
                />
              </div>

              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <div className="w-full flex col-span-1 col-start-1 md:col-start-0 md:col-span-2 lg:col-span-1 lg:col-start-3">
                  <NumericMaskedInput
                    name='fornTotal'
                    label='TOTAL'
                    value={total}
                    readOnly={true}
                    onChangeValue={() => { }}
                  />
                </div>
              </div>

              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <div className="w-full flex col-span-1 md:col-span-2 lg:col-span-1">
                  <DefaultDatePicker
                    name='fornDataEntrega'
                    label='DATA DE ENTREGA'
                    value={coletaPrecoFornecedor !== null ? coletaPrecoFornecedor.dataEntrega : null}
                    readOnly={status !== 0}
                    onChangeValue={e => {
                      if (e === null) onDataEntregaChange(null);
                      if (typeof e === 'object' && e instanceof Date) onDataEntregaChange(e);
                      if (typeof e === 'string' && e.length > 0) onDataEntregaChange(dayjs.utc(e).toDate());
                    }}
                  />
                </div>
              </div>

              {/*
              <DefaultInput
                name='fornPrazoEntrega'
                label='PRAZO DE ENTREGA'
                multiline={true}
                rows={5}
                readOnly={true}
                value={coletaPrecoFornecedor !== null ? coletaPrecoFornecedor.prazoEntrega : null}
                onChangeValue={e => onPrazoEntregaChange(e)}
              />
              */}

              <DefaultInput
                name='fornCodicoesPagamento'
                label='CONDIÇÕES DE PAGAMENTO'
                multiline={true}
                rows={5}
                readOnly={status !== 0}
                value={coletaPrecoFornecedor !== null ? coletaPrecoFornecedor.condicoesPagamento : null}
                onChangeValue={e => onCondicoesPagamentoChange(e)}
              />

              <DefaultInput
                name='fornObservacoes'
                label='Observações'
                multiline={true}
                rows={5}
                readOnly={status !== 0}
                value={coletaPrecoFornecedor !== null ? coletaPrecoFornecedor.observacoes : null}
                onChangeValue={e => onObservacoesChange(e)}
              />
            </div>
          }
        </Box >
      </Box >
    </div >
  );
}